import React from "react";
import {
  Box,
  Paper,
  Typography,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import { ErrorOutlineIcon, ExpandMoreIcon } from "../../icons";
import { useRouteError } from "react-router";
import { getRootStore } from "../../store/init";

/**
 * Contención de errores de render. Objetivo: que un error NO blanquee la app
 * (la pantalla cruda "Unexpected Application Error" de react-router), sino que
 * muestre un panel prolijo que NO oculta el error — lo presenta de forma
 * legible para que el usuario lo reporte al administrador del sistema.
 *
 * Se usa de dos formas:
 *  - `RouteErrorBoundary` como `errorElement` del router → captura errores de
 *    cualquier ruta (el caso común).
 *  - `AppErrorBoundary` (class) envolviendo el árbol → catch-all para errores
 *    fuera del router (providers, etc.).
 *
 * Es deliberadamente de dependencias mínimas: lee los textos de `langStore` de
 * forma defensiva (con fallback hardcodeado) para no volver a crashear si el
 * propio store está comprometido.
 */

const FALLBACK: Record<string, string> = {
  errorBoundaryTitle: "Ocurrió un error inesperado",
  errorBoundaryMessage:
    "No pudimos completar la operación. Por favor, contactá al administrador del sistema con el detalle de abajo.",
  errorBoundaryDetails: "Detalle técnico",
  errorBoundaryReload: "Recargar",
  errorBoundaryHome: "Ir al inicio",
};

const t = (key: keyof typeof FALLBACK): string => {
  try {
    const v = (getRootStore() as any)?.langStore?.[key];
    return typeof v === "string" && v ? v : FALLBACK[key];
  } catch {
    return FALLBACK[key];
  }
};

const formatError = (error: unknown): string => {
  if (!error) return "";
  if (error instanceof Error) return error.stack || error.message || String(error);
  if (typeof error === "object") {
    try {
      return JSON.stringify(error, null, 2);
    } catch {
      return String(error);
    }
  }
  return String(error);
};

const ErrorPanel: React.FC<{ error: unknown }> = ({ error }) => {
  const detail = formatError(error);
  return (
    <Box
      data-testid="error-boundary"
      sx={{
        minHeight: "60vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        p: 3,
      }}
    >
      <Paper elevation={2} sx={{ maxWidth: 640, width: "100%", p: 4, textAlign: "center" }}>
        <ErrorOutlineIcon color="error" sx={{ fontSize: 56, mb: 1 }} />
        <Typography variant="h5" gutterBottom>
          {t("errorBoundaryTitle")}
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          {t("errorBoundaryMessage")}
        </Typography>
        <Box sx={{ display: "flex", gap: 1, justifyContent: "center", mb: detail ? 2 : 0 }}>
          <Button variant="contained" onClick={() => window.location.reload()}>
            {t("errorBoundaryReload")}
          </Button>
          <Button
            variant="outlined"
            onClick={() => {
              window.location.href = "/";
            }}
          >
            {t("errorBoundaryHome")}
          </Button>
        </Box>
        {detail && (
          <Accordion sx={{ textAlign: "left", mt: 2, boxShadow: "none", border: "1px solid", borderColor: "divider" }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="body2" color="text.secondary">
                {t("errorBoundaryDetails")}
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Box
                component="pre"
                sx={{
                  whiteSpace: "pre-wrap",
                  wordBreak: "break-word",
                  fontSize: 12,
                  m: 0,
                  color: "text.secondary",
                }}
              >
                {detail}
              </Box>
            </AccordionDetails>
          </Accordion>
        )}
      </Paper>
    </Box>
  );
};

/** `errorElement` del router: captura errores de cualquier ruta descendiente. */
export const RouteErrorBoundary: React.FC = () => {
  const error = useRouteError();
  React.useEffect(() => {
    console.error("[RouteErrorBoundary]", error);
  }, [error]);
  return <ErrorPanel error={error} />;
};

/** Catch-all para errores fuera del router (providers, render del árbol superior). */
export class AppErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { error: unknown }
> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error: unknown) {
    return { error };
  }

  componentDidCatch(error: unknown, info: unknown) {
    console.error("[AppErrorBoundary]", error, info);
  }

  render() {
    if (this.state.error) return <ErrorPanel error={this.state.error} />;
    return this.props.children;
  }
}
